<script setup lang="ts">
import { computed, ref, watch } from "vue";
import SidebarNavigation from "../../sidebar/sidebarNavigation.vue";
import { useI18n } from "vue-i18n";
import InfoModal from "@/components/info/infoModal.vue";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    presets: any[];
    activeFilters: any;
  }>(),
  {
    presets: () => [],
    activeFilters: () => ({}),
  }
);

const emit = defineEmits<{
  (e: "updateFilters", value: any): void;
}>();

const filteredPresets = computed(() => {
  const stoneType = (props.activeFilters.type ?? props.activeFilters.stone_type) as string | undefined;
  const stoneForm = (props.activeFilters.form ?? props.activeFilters.stone_form) as string | undefined;

  const hasStoneForm = stoneForm !== undefined && stoneForm !== null && stoneForm !== "";
  const isNotBR = !!stoneForm && stoneForm !== "BR";
  const isEarthOrEmpty = !stoneType || stoneType === "earth";

  // Default behavior: show presets matching the current type
  return props.presets.filter((preset) => preset.filters.type === stoneType && preset.forms.includes(stoneForm));
});

const isPresetActive = (presetFilters: any) => {
  return Object.entries(presetFilters).every(([key, value]) => {
    const activeValue = props.activeFilters[key];
    if (Array.isArray(value) && Array.isArray(activeValue)) {
      return value.length === activeValue.length && value.every(v => activeValue.includes(v));
    }
    return activeValue === value;
  });
};

const activePreset = computed(() => {
  return filteredPresets.value.find(preset => isPresetActive(preset.filters));
});

const anyPresetActive = computed(() => !!activePreset.value);

const isOpen = ref(true);
const isExpanded = computed(() => anyPresetActive.value || isOpen.value);

const toggleOpen = () => {
  if (anyPresetActive.value) return;
  isOpen.value = !isOpen.value;
};

const applyPreset = (presetFilters: any) => {
  const newFilters = { ...props.activeFilters, ...presetFilters };
  emit('updateFilters', newFilters);
};

const itemRefs = ref<HTMLElement[]>([]);
const scrollContainer = ref<HTMLElement | null>(null);
const manualIndex = ref(0);

const currentIndex = computed({
  get: () => manualIndex.value,
  set: (val) => {
    manualIndex.value = val;
    scrollToIndex(val);
  }
});

const scrollToIndex = (index: number) => {
  const el = itemRefs.value[index];
  if (el && scrollContainer.value) {
    const container = scrollContainer.value;
    const elLeft = el.offsetLeft;
    const elWidth = el.clientWidth;
    const containerWidth = container.clientWidth;
    const targetScrollLeft = elLeft - (containerWidth / 2) + (elWidth / 2);
    container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  }
};

watch(activePreset, (preset) => {
  if (!preset) return;
  const idx = filteredPresets.value.indexOf(preset);
  if (idx >= 0) manualIndex.value = idx;
}, { immediate: true });

const onScroll = () => {
  if (!scrollContainer.value) return;
  const container = scrollContainer.value;
  const containerCenter = container.scrollLeft + container.clientWidth / 2;

  let closestIndex = 0;
  let minDistance = Infinity;

  itemRefs.value.forEach((el, index) => {
    if (!el) return;
    const elCenter = el.offsetLeft + el.clientWidth / 2;
    const distance = Math.abs(elCenter - containerCenter);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  manualIndex.value = closestIndex;
};
</script>

<template>
  <div class="presets-container w-full overflow-x-scroll" v-if="filteredPresets.length > 0">
    <div class="flex justify-between items-center cursor-pointer group" @click="toggleOpen">
      <div class="flex gap-2 items-center">
        <h3 class="font-bold text-sm text-gray-900 tracking-tight">Diamant Empfehlungen:</h3>
        <span v-if="activePreset" class="text-sm text-gray-500 font-light">{{ t('components.diamonds.search.presets.' + activePreset.qualityKey + '.title') }}</span>
        <info-modal id="stonePreset" :value="activeFilters.type"/>
      </div>
      <div v-if="!anyPresetActive" class="text-gray-400 group-hover:text-gray-500 transition-colors">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-4 h-4 transition-transform duration-300"
          :class="{ 'rotate-180': isExpanded }"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <div v-if="isExpanded" class="flex flex-col">
      <div
        ref="scrollContainer"
        class="mt-4 flex flex-nowrap gap-3 overflow-x-auto no-scrollbar pb-4 scroll-smooth w-full"
        @scroll="onScroll"
      >
        <button
          v-for="(preset, index) in filteredPresets"
          :key="preset.qualityKey"
          :ref="el => { if (el) itemRefs[index] = el as any }"
          @click="applyPreset(preset.filters)"
          type="button"
          class="shrink-0 w-[85px] justify-between flex flex-col cursor-pointer items-start p-0 rounded-xl border transition-all duration-300 text-left group relative overflow-hidden"
          :class="isPresetActive(preset.filters)
            ? 'border-brand-main bg-brand-main/5 ring-1 ring-brand-main'
            : 'border-gray-100 bg-white hover:border-brand-main/50 hover:shadow-md'"
        >
          <div class="flex justify-between items-start w-full mb-1 px-1 pt-2 ">
            <span class="w-full text-center text-sm font-bold tracking-tight" :class="isPresetActive(preset.filters) ? 'text-brand-main' : 'text-gray-900'">
              {{ t('components.diamonds.search.presets.' + preset.qualityKey + '.title') }}
            </span>
          </div>
          <p class="text-[8px] px-1  text-center w-full leading-relaxed text-gray-500 font-medium pb-1">
            {{ t('components.diamonds.search.presets.' + preset.qualityKey + '.description') }}
          </p>

          <p class="bg-brand-main text-[9px] py-1 text-center w-full leading-relaxed text-white font-light line-clamp-2">
            {{ t('components.diamonds.search.presets.' + preset.qualityKey + '.flag') }}
          </p>

          <!-- Subtle background accent for active state -->
          <div v-if="isPresetActive(preset.filters)" class="absolute -right-2 -bottom-2 w-12 h-12 bg-brand-main/10 rounded-full blur-2xl"></div>
        </button>
      </div>
      <sidebar-navigation
        :total="filteredPresets.length"
        v-model="currentIndex"
        :container="scrollContainer"
      />
    </div>
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>